import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Business } from '../types';
import { MapPin, Navigation, Copy, Check as CheckIcon, Shield, Zap, ExternalLink, Phone } from 'lucide-react';

import MarkerClusterGroup from '@changey/react-leaflet-markercluster';

import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

const MapPopupCard = React.memo(({ business }: { business: Business }) => {
  const [copied, setCopied] = useState(false);
  const getCategoryColor = (category: string) => {
    const colors = { restaurant: 'bg-emerald-500', tech: 'bg-blue-500', retail: 'bg-violet-500', service: 'bg-yellow-500', hotel: 'bg-pink-500', entertainment: 'bg-red-500' };
    return colors[category as keyof typeof colors] || 'bg-gray-500';
  };
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (business.address) { navigator.clipboard.writeText(business.address); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  const handleDirections = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${business.coordinates.lat},${business.coordinates.lng}`, '_blank');
  };
  return (
    <div className="relative w-80 bg-gray-900 rounded-lg border border-gray-700 overflow-hidden shadow-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-transparent to-orange-500/5 animate-pulse pointer-events-none" />
      <div className="relative p-3">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-white text-sm truncate group-hover:text-orange-300 transition-colors">{business.name}</h4>
            <div className="flex items-center gap-2 mt-1">
              <p className={`text-xs capitalize px-2 py-0.5 rounded-full text-white/90 font-medium ${getCategoryColor(business.category)}`}>{business.category}</p>
              {business.rating && (<div className="flex items-center gap-1 text-xs text-yellow-400"><span>★</span><span>{business.rating}</span>{business.reviews && <span className="text-gray-400">({business.reviews})</span>}</div>)}
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1 text-orange-400 font-bold text-xs bg-gray-800/50 px-2 py-1 rounded-full border border-orange-500/30"><span className="text-sm">ⓜ</span><span>XMR</span></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            {business.address && (<div className="p-2 bg-black/30 rounded-md border border-gray-700/50">
                <div className="flex items-start gap-1 text-gray-300 mb-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-orange-400" /><p className="text-xs flex-1 leading-tight">{business.address}</p></div>
                <div className="flex gap-1">
                  <button onClick={handleDirections} className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/40 transition-all duration-200 border border-blue-500/30"><Navigation className="w-3 h-3" /> Go</button>
                  <button onClick={handleCopy} className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600/50 text-gray-300 rounded hover:bg-gray-600 transition-all duration-200 border border-gray-600/50">{copied ? <CheckIcon className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}</button>
                </div>
            </div>)}
            <div className="space-y-1">
              {business.openHours && (<div className="flex items-center gap-1 text-xs text-gray-400"><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span><span className="truncate">{business.openHours}</span></div>)}
              <div className="flex items-center gap-2 text-xs">
                {business.website && (<a href={business.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors" onClick={(e) => e.stopPropagation()}><ExternalLink className="w-3 h-3" /><span>Site</span></a>)}
                {business.phone && (<a href={`tel:${business.phone}`} className="flex items-center gap-1 text-green-400 hover:text-green-300 transition-colors" onClick={(e) => e.stopPropagation()}><Phone className="w-3 h-3" /><span>Call</span></a>)}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <div className="flex items-center gap-1 mb-1"><Shield className="w-3 h-3 text-orange-500" /><span className="text-xs font-semibold text-gray-200">Payment</span></div>
              <div className="flex flex-wrap gap-1">
                {business.xmrPaymentMethods.slice(0, 3).map((method, index) => (<span key={method} className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded border border-orange-500/30 transition-all duration-200 hover:bg-orange-500/30" style={{ animationDelay: `${index * 100}ms` }}><Zap className="w-2 h-2" /><span className="truncate max-w-16">{method.split(' ')[0]}</span></span>))}
                {business.xmrPaymentMethods.length > 3 && (<span className="text-xs text-gray-400 px-1">+{business.xmrPaymentMethods.length - 3}</span>)}
              </div>
            </div>
            {business.description && (<div className="p-2 bg-black/20 rounded border border-gray-700/30"><p className="text-xs text-gray-300 leading-tight line-clamp-3">{business.description}</p></div>)}
          </div>
        </div>
      </div>
      <div className="absolute inset-0 rounded-lg border border-orange-500/20 animate-pulse pointer-events-none" />
    </div>
  );
});

const tileLayers = { dark: { id: 'dark', name: 'Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>' }, light: { id: 'light', name: 'Light', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>' }, satellite: { id: 'satellite', name: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles © Esri' } };
interface MapProps { businesses: Business[]; selectedBusiness: Business | null; onBusinessSelect: (business: Business | null) => void; viewport: { latitude: number; longitude: number; zoom: number }; loading?: boolean; onViewportChange?: (viewport: { latitude: number; longitude: number; zoom: number }) => void; mapStyle: string; onMapStyleChange: (styleId: string) => void; }

const MapBoundsHandler: React.FC = () => { const map = useMap(); useEffect(() => { const worldBounds = L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)); map.setMaxBounds(worldBounds); map.setMinZoom(2); map.setMaxZoom(18); }, [map]); return null; };
const createXMRIcon = (category: string) => {
    const colors = { restaurant: '#10b981', tech: '#3b82f6', retail: '#8b5cf6', service: '#eab308', hotel: '#ec4899', entertainment: '#ef4444' };
    const color = colors[category as keyof typeof colors] || '#6b7280';
    return L.divIcon({
        html: `<div class="marker-pin" style="background-color: ${color};"><span class="marker-symbol">ⓜ</span></div>`,
        className: 'xmr-marker-icon', iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
    });
};
const MemoizedMarker = React.memo(({ business, isSelected, onSelect }: { business: Business; isSelected: boolean; onSelect: (business: Business) => void; }) => {
    const markerRef = useRef<L.Marker>(null);
    const eventHandlers = useMemo(() => ({ click: () => onSelect(business) }), [business, onSelect]);
    const icon = useMemo(() => createXMRIcon(business.category), [business.category]);
    useEffect(() => {
        const marker = markerRef.current;
        if (marker && marker.getElement()) {
            const element = marker.getElement();
            if (isSelected) { element.classList.add('marker-selected'); } else { element.classList.remove('marker-selected'); }
        }
    }, [isSelected]);
    return (
        <Marker ref={markerRef} position={[business.coordinates.lat, business.coordinates.lng]} icon={icon} eventHandlers={eventHandlers}>
            <Popup className="landscape-popup" minWidth={320}><MapPopupCard business={business} /></Popup>
        </Marker>
    );
});
const MapEventsManager: React.FC<{
  onViewportChange?: (viewport: { latitude: number; longitude: number; zoom: number }) => void;
  isProgrammaticMove: React.MutableRefObject<boolean>;
}> = ({ onViewportChange, isProgrammaticMove }) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const debouncedViewportChange = useCallback((map: L.Map) => {
    if (!onViewportChange) return;
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); }
    timeoutRef.current = setTimeout(() => {
        const center = map.getCenter();
        const zoom = map.getZoom();
        onViewportChange({ latitude: center.lat, longitude: center.lng, zoom: zoom });
    }, 800);
  }, [onViewportChange]);
  const eventHandler = (map: L.Map) => {
    if (isProgrammaticMove.current) { isProgrammaticMove.current = false; return; }
    debouncedViewportChange(map);
  }
  useMapEvents({ moveend: (e) => eventHandler(e.target), zoomend: (e) => eventHandler(e.target), });
  useEffect(() => () => { if (timeoutRef.current) { clearTimeout(timeoutRef.current); } }, []);
  return null;
};

export const Map: React.FC<MapProps> = ({ businesses, selectedBusiness, onBusinessSelect, viewport, loading, onViewportChange, mapStyle, onMapStyleChange }) => {
  const mapRef = useRef<L.Map>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeLayer = tileLayers[mapStyle as keyof typeof tileLayers] || tileLayers.dark;
  const isProgrammaticMove = useRef(false);

  useEffect(() => {
    if (mapRef.current && selectedBusiness) {
        isProgrammaticMove.current = true;
        mapRef.current.flyTo([selectedBusiness.coordinates.lat, selectedBusiness.coordinates.lng], mapRef.current.getZoom() < 14 ? 14 : mapRef.current.getZoom(), { animate: true, duration: 1.5 });
    }
  }, [selectedBusiness]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const invalidateMapSize = () => {
        map.invalidateSize();
    };

    const mountTimeout = setTimeout(invalidateMapSize, 500);

    if (!containerRef.current) return;
    const observer = new ResizeObserver(invalidateMapSize);
    observer.observe(containerRef.current);

    return () => {
        clearTimeout(mountTimeout);
        observer.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="w-full h-screen bg-gray-900 relative">
      {loading && (
        <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-center z-[2000] text-white transition-opacity duration-300">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <span>Loading businesses...</span>
          </div>
        </div>
      )}
        <style>{`
            .leaflet-zoom-anim .leaflet-zoom-animated { transition: transform 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important; }
            .landscape-popup .leaflet-popup-content-wrapper { background: transparent; box-shadow: none; border: none; padding: 0; border-radius: 8px; }
            .landscape-popup .leaflet-popup-content { margin: 0; width: auto !important; max-width: none !important; }
            .landscape-popup .leaflet-popup-tip { background: #111827; border: 1px solid #374151; box-shadow: none; }
            .landscape-popup .leaflet-popup-close-button { color: #9CA3AF; font-size: 14px; padding: 4px 8px; background: rgba(17, 24, 39, 0.8); border-radius: 0 8px 0 4px; border: none; backdrop-filter: blur(8px); }
            .landscape-popup .leaflet-popup-close-button:hover { color: #F97316; background: rgba(249, 115, 22, 0.2); }
            .xmr-marker-icon .marker-pin { border: 3px solid #ff6600; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 8px rgba(0,0,0,0.3); width: 100%; height: 100%; transition: all 0.3s ease; }
            .xmr-marker-icon .marker-symbol { color: white; font-weight: bold; font-size: 12px; transform: rotate(45deg); transition: font-size 0.3s ease; }
            .xmr-marker-icon.marker-selected { width: 40px !important; height: 40px !important; margin-left: -20px !important; margin-top: -40px !important; z-index: 1000 !important; animation: pulse 1.5s infinite; }
            .xmr-marker-icon.marker-selected .marker-symbol { font-size: 16px; }
            @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        `}</style>
        
        <MapContainer center={[viewport.latitude, viewport.longitude]} zoom={viewport.zoom} style={{ height: '100%', width: '100%', backgroundColor: '#111827' }} ref={mapRef} className="z-0" zoomControl={true} >
            <TileLayer
                url={activeLayer.url}
                attribution={activeLayer.attribution}
                eventHandlers={{
                    tileerror: (event) => {
                        console.log('TILE LOAD ERROR:', {
                            message: 'A map tile failed to load.',
                            url: event.tile.src,
                            coords: event.coords,
                            error: event.error,
                        });
                    },
                }}
            />
            <MapEventsManager onViewportChange={onViewportChange} isProgrammaticMove={isProgrammaticMove} />
            <MapBoundsHandler />
            <MarkerClusterGroup chunkedLoading maxClusterRadius={60}>
                {businesses.map((business) => (
                    <MemoizedMarker key={business.id} business={business} isSelected={selectedBusiness?.id === business.id} onSelect={onBusinessSelect} />
                ))}
            </MarkerClusterGroup>
        </MapContainer>
    </div>
  );
};
